import { Loading } from '@/common/atoms/Loading'
import TextError from '@/common/atoms/TextError'
import {
  Application_Status_Enum,
  EstateForSalePageFragment,
  useApplicationsSubscription,
} from '@gql'
import React, { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ApplicationsCoreTable } from './ApplicationsCoreTable'
import { ApplicationsTableToolbar } from './ApplicationsTableToolbar'
import { columns } from './ApplicationsTableColumns'
import ApplicationSheet from '@/application/components/sheet/ApplicationSheet'
import useIsAgencyMember from '@/agencyMember/hooks/useIsAgencyMember'

type ApplicationsTableProps = {
  estate: EstateForSalePageFragment
}

export const ApplicationsTable = ({ estate }: ApplicationsTableProps) => {
  const { t } = useTranslation()
  const [showNonEditedApplications, setShowNonEditedApplications] =
    useState<boolean>(false)

  const [openedApplicationIndex, setOpenedApplicationIndex] =
    useState<number>(-1)

  const openApplication = useCallback((applicationIndex: number) => {
    setOpenedApplicationIndex(applicationIndex)
  }, [])
  const isAdmin = useIsAgencyMember()
  const cols = useMemo(
    () => columns(t, openApplication, isAdmin ?? false),
    [t, openApplication, isAdmin]
  )

  const { data, error, loading } = useApplicationsSubscription({
    variables: { estateId: estate.id },
  })

  const applications = useMemo(() => {
    return (
      data?.application.filter(
        (application) =>
          showNonEditedApplications ||
          application.hasDraftBeenEdited ||
          // Also show applications when the visit has been booked, even if not edited
          application.buyer_member.visits.length > 0
      ) ?? []
    )
  }, [data, showNonEditedApplications])

  const estateRoundNumbers = useMemo(() => {
    if (!data?.application) return []
    // Get all round numbers that are not null/undefined, and filter uniques
    const numbers = data.application
      .map((application) => application.round?.number)
      .filter((number): number is number => number != null)
    // Return unique sorted round numbers
    return Array.from(new Set(numbers)).sort((a, b) => a - b)
  }, [data])

  const someApplicationIsAccepted = useMemo(() => {
    return applications.some(
      (application) => application.status === Application_Status_Enum.Accepted
    )
  }, [applications])

  const openedApplication = useMemo(() => {
    return applications[openedApplicationIndex]
  }, [openedApplicationIndex, applications])

  const canNext = useMemo(() => {
    return openedApplicationIndex < applications.length - 1
  }, [openedApplicationIndex, applications])

  const canPrev = useMemo(() => {
    return openedApplicationIndex > 0
  }, [openedApplicationIndex])

  const handleNext = useCallback(() => {
    if (canNext) {
      setOpenedApplicationIndex(openedApplicationIndex + 1)
    }
  }, [canNext, openedApplicationIndex])

  const handlePrev = useCallback(() => {
    if (canPrev) {
      setOpenedApplicationIndex(openedApplicationIndex - 1)
    }
  }, [canPrev, openedApplicationIndex])

  const handleOpenChange = useCallback((open: boolean) => {
    open
      ? setOpenedApplicationIndex(openedApplicationIndex)
      : setOpenedApplicationIndex(-1)
  }, [])

  return (
    <>
      <Loading center active={loading} />
      {error && <TextError error={error} />}
      {applications && (
        <ApplicationsCoreTable
          columns={cols}
          data={applications}
          toolbar={(table, grouping, setGrouping) => (
            <ApplicationsTableToolbar
              table={table}
              grouping={grouping}
              setGrouping={setGrouping}
              setShowNonEditedApplications={setShowNonEditedApplications}
              showNonEditedApplications={showNonEditedApplications}
              estateRoundNumbers={estateRoundNumbers}
            />
          )}
        />
      )}
      {openedApplication && (
        <ApplicationSheet
          open={openedApplicationIndex !== -1}
          onOpenChange={handleOpenChange}
          onNext={handleNext}
          onPrevious={handlePrev}
          estate={estate}
          isAdmin={isAdmin ?? false}
          application={openedApplication}
          someApplicationIsAccepted={someApplicationIsAccepted}
        />
      )}
    </>
  )
}
